import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import EtContentEditorLight3 from "@/utils/EtContentEditorLight3"
import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline"
import {
  useGetOrganizationBUsQuery,
  useUpdateOrganizationBUMutation,
} from "./organizationBUsApiSlice"

const EtUpdateOrganizationBU = ({ id, setActiveAdminView, userOrgId = "" }) => {
  // First Created - 27 Apr 2026
  // Author - Atul Pandey
  // Purpose - To update an Organization BU

  useTitle("Update Organization BU")

  const { organizationBU } = useGetOrganizationBUsQuery(userOrgId, {
    selectFromResult: ({ data }) => ({
      organizationBU: data?.entities[id],
    }),
  })

  if (!organizationBU) {
    return <PulseLoader color={"#ea580c"} />
  }

  return (
    <EtUpdateOrganizationBUForm
      organizationBU={organizationBU}
      id={id}
      userOrgId={userOrgId}
      setActiveAdminView={setActiveAdminView}
    />
  )
}

const EtUpdateOrganizationBUForm = ({
  organizationBU,
  id,
  userOrgId = "",
  setActiveAdminView,
}) => {
  // First Created - 27 Apr 2026
  // Author - Atul Pandey
  // Purpose - To render Organization BU update form

  const navigate = useNavigate()

  const [
    updateOrganizationBU,
    { isLoading, isSuccess, isError, error },
  ] = useUpdateOrganizationBUMutation()

  const [formData, setFormData] = useState({
    ownerOrgId: organizationBU.ownerOrgId?._id || organizationBU.ownerOrgId || userOrgId,
    organizationBUName: organizationBU.organizationBUName || "",
    organizationBUCode: organizationBU.organizationBUCode || "",
    organizationBUDesc: organizationBU.organizationBUDesc || "",
    organizationBUPurpose: organizationBU.organizationBUPurpose || "",
    organizationBUIntro: organizationBU.organizationBUIntro || "",
    organizationBUOutro: organizationBU.organizationBUOutro || "",
    organizationBULocations: organizationBU.organizationBULocations?.length
      ? organizationBU.organizationBULocations
      : [{ location: "" }],
    organizationBUTargetAudience: organizationBU.organizationBUTargetAudience?.length
      ? organizationBU.organizationBUTargetAudience
      : [{ audience: "" }],
    organizationBUTeamSize: organizationBU.organizationBUTeamSize || "",
    organizationBURevenueSize: organizationBU.organizationBURevenueSize || "",
    organizationBUStartedDate: organizationBU.organizationBUStartedDate
      ? organizationBU.organizationBUStartedDate?.slice(0, 10)
      : "",
    organizationBURecognitions: organizationBU.organizationBURecognitions?.length
      ? organizationBU.organizationBURecognitions
      : [{ recognition: "" }],
    organizationBUContributions: organizationBU.organizationBUContributions?.length
      ? organizationBU.organizationBUContributions
      : [{ contribution: "" }],
    aboutMe: organizationBU.aboutMe || "",
    pic: "",
    active: organizationBU.active ?? true,
  })

  useEffect(() => {
    if (isSuccess) {
      setFormData({
        ownerOrgId: userOrgId,
        organizationBUName: "",
        organizationBUCode: "",
        organizationBUDesc: "",
        organizationBUPurpose: "",
        organizationBUIntro: "",
        organizationBUOutro: "",
        organizationBULocations: [{ location: "" }],
        organizationBUTargetAudience: [{ audience: "" }],
        organizationBUTeamSize: "",
        organizationBURevenueSize: "",
        organizationBUStartedDate: "",
        organizationBURecognitions: [{ recognition: "" }],
        organizationBUContributions: [{ contribution: "" }],
        aboutMe: "",
        pic: "",
        active: true,
      })

      if (setActiveAdminView) {
        setActiveAdminView("organizationBUs")
      } else {
        navigate("/organizationBUs")
      }
    }
  }, [isSuccess])

  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target

    if (name === "pic") {
      setFormData(prevState => ({ ...prevState, pic: files[0] }))
    } else if (type === "checkbox") {
      setFormData(prevState => ({ ...prevState, [name]: checked }))
    } else {
      setFormData(prevState => ({ ...prevState, [name]: value }))
    }
  }

  const handleAdd = (fieldName, newItem) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: [...prevState[fieldName], newItem],
    }))
  }

  const handleRemove = (fieldName, index) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: prevState[fieldName].filter((_, i) => i !== index),
    }))
  }

  const handleFieldChange = (fieldName, index, value) => {
    const updatedItems = formData[fieldName].map((item, i) =>
      i === index ? value : item
    )

    setFormData({
      ...formData,
      [fieldName]: updatedItems,
    })
  }

  const handleAddAudience = () =>
    handleAdd("organizationBUTargetAudience", { audience: "" })

  const handleRemoveAudience = (index) =>
    handleRemove("organizationBUTargetAudience", index)

  const handleAudienceChange = (index, value) =>
    handleFieldChange("organizationBUTargetAudience", index, { audience: value })

  const handleAddLocations = () =>
    handleAdd("organizationBULocations", { location: "" })

  const handleRemoveLocations = (index) =>
    handleRemove("organizationBULocations", index)

  const handleLocationsChange = (index, value) =>
    handleFieldChange("organizationBULocations", index, { location: value })

  const handleAddRecognitions = () =>
    handleAdd("organizationBURecognitions", { recognition: "" })

  const handleRemoveRecognitions = (index) =>
    handleRemove("organizationBURecognitions", index)

  const handleRecognitionsChange = (index, value) =>
    handleFieldChange("organizationBURecognitions", index, {
      recognition: value,
    })

  const handleAddContributions = () =>
    handleAdd("organizationBUContributions", { contribution: "" })

  const handleRemoveContributions = (index) =>
    handleRemove("organizationBUContributions", index)

  const handleContributionsChange = (index, value) =>
    handleFieldChange("organizationBUContributions", index, {
      contribution: value,
    })

  const handleSubmit = async (e) => {
    e.preventDefault()

    const organizationBUData = {
      id,
      ownerOrgId: formData.ownerOrgId,
      organizationBUName: formData.organizationBUName,
      organizationBUCode: formData.organizationBUCode,
      organizationBUDesc: formData.organizationBUDesc,
      organizationBUPurpose: formData.organizationBUPurpose,
      organizationBUIntro: formData.organizationBUIntro,
      organizationBUOutro: formData.organizationBUOutro,
      organizationBULocations: formData.organizationBULocations,
      organizationBUTargetAudience: formData.organizationBUTargetAudience,
      organizationBUTeamSize: formData.organizationBUTeamSize,
      organizationBURevenueSize: formData.organizationBURevenueSize,
      organizationBUStartedDate: formData.organizationBUStartedDate,
      organizationBURecognitions: formData.organizationBURecognitions,
      organizationBUContributions: formData.organizationBUContributions,
      aboutMe: formData.aboutMe,
      active: formData.active,
    }

    if (formData.pic) {
      const reader = new FileReader()

      reader.onloadend = async () => {
        organizationBUData.pic = reader.result

        try {
          await updateOrganizationBU(organizationBUData).unwrap()
        } catch (error) {
          console.error(error)
        }
      }

      reader.readAsDataURL(formData.pic)
    } else {
      try {
        await updateOrganizationBU(organizationBUData).unwrap()
      } catch (error) {
        console.error(error)
      }
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("organizationBUs")
    } else {
      navigate("/organizationBUs")
    }
  }

  const handleClear = () => {
    setFormData({
      ownerOrgId: userOrgId,
      organizationBUName: "",
      organizationBUCode: "",
      organizationBUDesc: "",
      organizationBUPurpose: "",
      organizationBUIntro: "",
      organizationBUOutro: "",
      organizationBULocations: [{ location: "" }],
      organizationBUTargetAudience: [{ audience: "" }],
      organizationBUTeamSize: "",
      organizationBURevenueSize: "",
      organizationBUStartedDate: "",
      organizationBURecognitions: [{ recognition: "" }],
      organizationBUContributions: [{ contribution: "" }],
      aboutMe: "",
      pic: "",
      active: true,
    })
  }

  return (
    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Update Organization BU
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
            <div id="organizationBUName">
              <input
                type="text"
                name="organizationBUName"
                placeholder="Enter the BU name here"
                value={formData.organizationBUName}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="organizationBUCode">
              <input
                type="text"
                name="organizationBUCode"
                placeholder="Enter the BU code here"
                value={formData.organizationBUCode}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="organizationBUDesc">
              <input
                type="text"
                name="organizationBUDesc"
                placeholder="Enter the BU description here"
                value={formData.organizationBUDesc}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="organizationBUStartedDate">
              <input
                type="date"
                name="organizationBUStartedDate"
                value={formData.organizationBUStartedDate}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="organizationBUTeamSize">
              <input
                type="text"
                name="organizationBUTeamSize"
                placeholder="Enter the BU team size"
                value={formData.organizationBUTeamSize}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="organizationBURevenueSize">
              <input
                type="text"
                name="organizationBURevenueSize"
                placeholder="Enter the BU revenue size"
                value={formData.organizationBURevenueSize}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div className="bg-white w-full rounded p-2 m-1">
              <EtContentEditorLightWithPlace
                value={formData.organizationBUIntro}
                handleChange={(content) =>
                  setFormData({ ...formData, organizationBUIntro: content })
                }
                placeholderText={"Write what people would see when this BU starts!"}
              />
            </div>

            <div className="bg-white w-full rounded p-2 m-1">
              <EtContentEditorLight3
                value={formData.organizationBUOutro}
                handleChange={(content) =>
                  setFormData({ ...formData, organizationBUOutro: content })
                }
                placeholderText={"Write what people would see when this BU ends!"}
                toolbarId={"organizationBUOutro"}
              />
            </div>

            <div id="aboutMe">
              <input
                type="text"
                name="aboutMe"
                placeholder="Add something about the personality of this BU"
                value={formData.aboutMe}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="pic">
              <input
                type="file"
                name="pic"
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div className="flex justify-start items-center bg-white rounded m-1 p-2">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                className="m-1"
              />
              <p className="text-xs font-bold text-gray-700 m-1">
                Active BU
              </p>
            </div>

            <div className="m-1 p-1 border border-white rounded">
              <div>
                <p className="font-bold mx-2 px-2 mt-2">Target Audience</p>
                <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                  Who this BU is focused on?
                </p>
              </div>

              {formData.organizationBUTargetAudience.map((item, index) => (
                <div key={index} className="flex justify-start items-center mx-1">
                  <input
                    type="text"
                    value={item.audience}
                    onChange={(e) =>
                      handleAudienceChange(index, e.target.value)
                    }
                    placeholder="Enter a target audience"
                    className="w-full rounded p-2 m-1 text-xs"
                  />

                  <div className="rounded bg-red-500 m-1 p-1">
                    <button
                      type="button"
                      onClick={() => handleRemoveAudience(index)}
                    >
                      <MinusIcon className="h-5 w-5 text-white" />
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex justify-start items-center m-1">
                <p className="font-bold text-xs m-1 p-1">Add</p>
                <button type="button" onClick={handleAddAudience}>
                  <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />
                </button>
              </div>
            </div>

            <div className="m-1 p-1 border border-white rounded">
              <div>
                <p className="font-bold mx-2 px-2 mt-2">BU Locations</p>
                <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                  What are the locations for this BU?
                </p>
              </div>

              {formData.organizationBULocations.map((item, index) => (
                <div key={index} className="flex justify-start items-center mx-1">
                  <input
                    type="text"
                    value={item.location}
                    onChange={(e) =>
                      handleLocationsChange(index, e.target.value)
                    }
                    placeholder="Enter a BU location"
                    className="w-full rounded p-2 m-1 text-xs"
                  />

                  <div className="rounded bg-red-500 m-1 p-1">
                    <button
                      type="button"
                      onClick={() => handleRemoveLocations(index)}
                    >
                      <MinusIcon className="h-5 w-5 text-white" />
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex justify-start items-center m-1">
                <p className="font-bold text-xs m-1 p-1">Add</p>
                <button type="button" onClick={handleAddLocations}>
                  <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />
                </button>
              </div>
            </div>

            <div className="m-1 p-1 border border-white rounded">
              <div>
                <p className="font-bold mx-2 px-2 mt-2">BU Recognitions</p>
                <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                  What recognitions are associated with this BU?
                </p>
              </div>

              {formData.organizationBURecognitions.map((item, index) => (
                <div key={index} className="flex justify-start items-center mx-1">
                  <input
                    type="text"
                    value={item.recognition}
                    onChange={(e) =>
                      handleRecognitionsChange(index, e.target.value)
                    }
                    placeholder="Enter a BU recognition"
                    className="w-full rounded p-2 m-1 text-xs"
                  />

                  <div className="rounded bg-red-500 m-1 p-1">
                    <button
                      type="button"
                      onClick={() => handleRemoveRecognitions(index)}
                    >
                      <MinusIcon className="h-5 w-5 text-white" />
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex justify-start items-center m-1">
                <p className="font-bold text-xs m-1 p-1">Add</p>
                <button type="button" onClick={handleAddRecognitions}>
                  <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />
                </button>
              </div>
            </div>

            <div className="m-1 p-1 border border-white rounded">
              <div>
                <p className="font-bold mx-2 px-2 mt-2">BU Contributions</p>
                <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                  What does this BU contribute to the organization?
                </p>
              </div>

              {formData.organizationBUContributions.map((item, index) => (
                <div key={index} className="flex justify-start items-center mx-1">
                  <input
                    type="text"
                    value={item.contribution}
                    onChange={(e) =>
                      handleContributionsChange(index, e.target.value)
                    }
                    placeholder="Enter a BU contribution"
                    className="w-full rounded p-2 m-1 text-xs"
                  />

                  <div className="rounded bg-red-500 m-1 p-1">
                    <button
                      type="button"
                      onClick={() => handleRemoveContributions(index)}
                    >
                      <MinusIcon className="h-5 w-5 text-white" />
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex justify-start items-center m-1">
                <p className="font-bold text-xs m-1 p-1">Add</p>
                <button type="button" onClick={handleAddContributions}>
                  <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />
                </button>
              </div>
            </div>

            <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Update"}
                type={"create"}
                handleClick={handleSubmit}
              />

              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EtUpdateOrganizationBU